// Word Builder: snap prefixes, bases, suffixes and Greek and Latin roots together.
// Every answer ends with one line that explains the parts.

import { h, clear } from '@kit/dom';
import type { GameModule, GameContext } from '@shell/types';
import { makeItem, isRealWord, joinSuffix, ROOT_BY_ID, SUFFIXES, type Item } from '@content/words/morphemes';

const ITEMS = 8;

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let level = ctx.currentLevel();
  const used = new Set<string>();
  let it: Item = makeItem(level, ctx.rng, used);
  let attempts = 0, correct = 0, minLevel = level;
  let wrongs = 0, hinted = false, locked = false;
  let pickA: string | null = null, pickB: string | null = null;   // tile picks
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Word ', h('strong', null, '1'), ` of ${ITEMS}`), h('button.btn.small.ghost', { onclick: hint }, 'Hint'));
  const prompt = h('p.center', { style: { fontWeight: '700', minHeight: '44px', padding: '2px 4px', fontSize: '19px' } });
  const built = h('div.prompt', { style: { minHeight: '48px', letterSpacing: '1px' } });
  const area = h('div.stack', { style: { gap: '8px' } });
  const explain = h('p.center.muted.small', { style: { minHeight: '22px' } });
  host.append(head, prompt, built, area, explain);

  function setup() {
    level = ctx.currentLevel(); minLevel = Math.min(minLevel, level);
    it = makeItem(level, ctx.rng, used);
    wrongs = 0; hinted = false; locked = false; pickA = null; pickB = null;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    prompt.textContent = it.prompt;
    explain.textContent = '';
    if (it.speak) ctx.speak(it.speak.replace(/___/g, 'blank'));
    render();
  }

  function render() {
    clear(area);
    if (it.kind === 'build' && it.tiles && 'bases' in it.tiles) {
      built.textContent = (pickA ?? '____') + ' + ' + (pickB ?? '____');
      area.append(h('p.muted.small.center', null, 'Pick a base word, then an ending.'));
      area.append(h('div.grid3', null, ...it.tiles.bases.map(b => h('button.choice', { disabled: locked, style: { fontSize: '18px', borderColor: pickA === b ? 'var(--words)' : '' }, onclick: () => { pickA = b; render(); tryBuild(); } }, b))));
      area.append(h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' } }, ...it.tiles.suffixes.map(s => h('button.choice', { disabled: locked, style: { fontSize: '18px', borderColor: pickB === s ? 'var(--words)' : '' }, onclick: () => { pickB = s; render(); tryBuild(); } }, '-' + s))));
    } else if (it.kind === 'greek' && it.tiles && 'roots' in it.tiles) {
      built.textContent = (pickA ?? '____') + ' + ' + (pickB ?? '____');
      area.append(h('p.muted.small.center', null, 'Pick two roots in order.'));
      area.append(h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' } }, ...it.tiles.roots.map(r => h('button.choice', {
        disabled: locked,
        style: { fontSize: '17px', minHeight: '64px', display: 'flex', flexDirection: 'column', gap: '2px', borderColor: pickA === r || pickB === r ? 'var(--words)' : '' },
        onclick: () => { if (pickA === null) pickA = r; else if (pickB === null && r !== pickA) pickB = r; else { pickA = r; pickB = null; } render(); tryBuild(); },
      }, h('span', null, r), h('span.muted', { style: { fontSize: '11px', fontWeight: '600' } }, ROOT_BY_ID[r]?.meaning ?? '')))));
    } else {
      built.textContent = it.kind === 'prefix' ? '' : '';
      built.style.minHeight = it.kind === 'prefix' ? '0' : '48px';
      area.append(h('div.choices', { style: it.choices.length === 2 ? { gridTemplateColumns: '1fr 1fr' } : {} }, ...it.choices.map(c => h('button.choice', { disabled: locked, style: { fontSize: it.kind === 'define' ? '15px' : '20px', minHeight: it.kind === 'define' ? '64px' : '56px', padding: '6px 10px' }, onclick: () => answer(c) }, c))));
    }
  }

  function tryBuild() {
    if (pickA === null || pickB === null) return;
    const word = it.kind === 'greek' ? pickA + pickB : joinSuffix(pickA, pickB);
    built.textContent = word;
    answer(word);
  }

  function answer(v: string) {
    if (locked) return;
    const ms = performance.now() - tProblem;
    const ok = v.toLowerCase() === it.answer.toLowerCase();
    if (ok) {
      locked = true; attempts++; correct++;
      ctx.report({ skill: it.kind === 'greek' || it.kind === 'define' || it.kind === 'prefix' ? 'vocabulary' : 'phonics_spelling', level, correct: true, hintUsed: hinted, ms, problemId: `wb:${it.kind}:${it.answer}` });
      ctx.sound('ding');
      explain.textContent = it.explain;
      ctx.say(`${it.answer}! Yes.`);
      next();
    } else {
      wrongs++;
      ctx.sound('buzz');
      built.classList.remove('shake'); void built.offsetWidth; built.classList.add('shake');
      if (it.kind === 'build' || it.kind === 'greek') {
        const real = isRealWord(v);
        ctx.say(real ? `"${v}" is a real word, but it does not mean that. Try again.` : `"${v}" is not a word. Try another pair.`);
        pickA = null; pickB = null; render();
      } else {
        ctx.say(`Not ${v}. Try again.`);
      }
      if (wrongs >= 2) {
        attempts++;
        ctx.report({ skill: 'vocabulary', level, correct: false, hintUsed: hinted, ms, problemId: `wb:${it.kind}:${it.answer}` });
        locked = true;
        explain.textContent = `It was "${it.answer}". ${it.explain}`;
        next(1700);
      }
    }
  }

  function hint() {
    if (locked) return;
    hinted = true;
    if (it.kind === 'build' && it.tiles && 'bases' in it.tiles) { const s = it.answer; const suf = it.tiles.suffixes.find(x => s.endsWith(x.replace(/^i/, '')) || s.endsWith(x)); ctx.say(`Hint: the ending is -${suf ?? it.tiles.suffixes[0]}. ${SUFFIXES.find(x => x.a === suf)?.meaning ?? ''}`); }
    else if (it.kind === 'greek') ctx.say(`Hint: it starts with "${it.answer.slice(0, 3)}".`);
    else if (it.kind === 'judge') ctx.say('Hint: say it out loud. Would you ever hear a grown-up say it?');
    else ctx.say(`Hint: it starts with "${it.answer[0]}".`);
  }

  function next(delay = 1300) {
    n++;
    if (n >= ITEMS) {
      if (attempts === correct && minLevel >= 7) ctx.charm('charm.roots');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), delay);
      return;
    }
    setTimeout(setup, delay);
  }

  setup();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'words.word-builder',
  title: 'Word Builder',
  blurb: 'Snap beginnings, roots and endings together. Every part means something.',
  category: 'words',
  skills: ['phonics_spelling', 'vocabulary'],
  levelRange: [1, 10],
  unlock: { stars: 14 },
  mount,
};
export default mod;
